import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MapPin,
  Navigation,
  Search,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  Loader2,
  Star,
  Calendar,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { sitesService } from '../../services/sitesService';
import { api } from '../../services/api';

type Site = {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  distance?: number;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  lastInspection?: string;
  status: 'active' | 'pending' | 'completed';
  source?: 'assigned' | 'scheduled' | 'search';
};

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

export function LiteStartInspectionPage() {
  const navigate = useNavigate();
  const [userLocation, setUserLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);

  const debouncedSearch = useDebounce(searchTerm.trim(), 300);

  // Fetch assigned sites (always shown when no search)
  const { data: assignedData, isLoading: isLoadingAssigned } = useQuery({
    queryKey: ['my-assigned-sites'],
    queryFn: async () => {
      const response = await api.get<{
        items: Array<{
          id: string;
          name: string;
          city: string;
          state: string;
          latitude: number;
          longitude: number;
          last_inspection_date: string | null;
          inspection_frequency: string;
          is_overdue: boolean;
          days_overdue: number | null;
        }>;
        total: number;
      }>('/sites/my-assigned-open');
      return response.data;
    },
  });

  // Fetch today's scheduled sites
  const { data: scheduleData } = useQuery({
    queryKey: ['today-schedule-sites'],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get<{
        week_start: string;
        week_end: string;
        days: Array<{
          date: string;
          sites: Array<{
            site_id: string;
            site_name: string;
            city: string;
            state: string;
            latitude: number;
            longitude: number;
          }>;
        }>;
      }>(`/schedules/weekly/plan?week_date=${today}`);
      return response.data;
    },
  });

  // Server-side search when user types
  const { data: searchData, isLoading: isSearching } = useQuery({
    queryKey: ['sites-search', debouncedSearch],
    queryFn: () =>
      sitesService.getSites({
        search: debouncedSearch,
        status: 'active',
        size: 20,
      }),
    enabled: debouncedSearch.length >= 2,
  });

  useEffect(() => {
    getCurrentLocation();
  }, []);

  const getCurrentLocation = () => {
    setIsLoadingLocation(true);
    setLocationError(null);
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setIsLoadingLocation(false);
        },
        (error) => {
          let errorMessage = 'Unable to get your location.';
          if (error.code === error.PERMISSION_DENIED) {
            errorMessage =
              'Location permission denied. Please enable location in your browser settings.';
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            errorMessage = 'Location unavailable. Please try again.';
          } else if (error.code === error.TIMEOUT) {
            errorMessage = 'Location request timed out. Please try again.';
          }
          setLocationError(errorMessage);
          setIsLoadingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000,
        }
      );
    } else {
      setLocationError('Geolocation is not supported by your browser.');
      setIsLoadingLocation(false);
    }
  };

  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ) => {
    const R = 3959;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const addDistance = (site: Site): Site => ({
    ...site,
    distance: userLocation
      ? calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          site.coordinates.latitude,
          site.coordinates.longitude
        )
      : undefined,
  });

  // Build assigned sites list
  const assignedSites: Site[] = useMemo(
    () =>
      (assignedData?.items || []).map((s) => ({
        id: s.id,
        name: s.name,
        address: '',
        city: s.city || '',
        state: s.state || '',
        coordinates: {
          latitude: s.latitude || 0,
          longitude: s.longitude || 0,
        },
        lastInspection: s.last_inspection_date || undefined,
        status: 'active' as const,
        source: 'assigned' as const,
      })),
    [assignedData]
  );

  // Build today's scheduled sites
  const scheduledSites: Site[] = useMemo(() => {
    if (!scheduleData?.days) return [];
    const today = new Date().toISOString().split('T')[0];
    const todayDay = scheduleData.days.find((d) => d.date === today);
    if (!todayDay) return [];
    return todayDay.sites.map((s) => ({
      id: s.site_id,
      name: s.site_name,
      address: '',
      city: s.city || '',
      state: s.state || '',
      coordinates: {
        latitude: s.latitude || 0,
        longitude: s.longitude || 0,
      },
      status: 'active' as const,
      source: 'scheduled' as const,
    }));
  }, [scheduleData]);

  // Build search results
  const searchSites: Site[] = useMemo(
    () =>
      (searchData?.items || []).map((s) => ({
        id: s.id,
        name: s.name,
        address: s.address?.street1 || '',
        city: s.address?.city || '',
        state: s.address?.state || '',
        coordinates: {
          latitude: s.coordinates?.latitude || 0,
          longitude: s.coordinates?.longitude || 0,
        },
        lastInspection:
          s.inspection_schedule?.last_inspection_date || undefined,
        status: s.status as 'active' | 'pending' | 'completed',
        source: 'search' as const,
      })),
    [searchData]
  );

  // Determine which sites to show
  const isSearchActive = debouncedSearch.length >= 2;
  const displaySites: Site[] = useMemo(() => {
    if (isSearchActive) {
      return searchSites.map(addDistance);
    }

    // Merge assigned + scheduled, dedup by ID, sort by distance
    const seen = new Set<string>();
    const merged: Site[] = [];

    // Scheduled sites first (today's priority)
    for (const site of scheduledSites) {
      if (!seen.has(site.id)) {
        seen.add(site.id);
        merged.push(site);
      }
    }

    // Then assigned sites
    for (const site of assignedSites) {
      if (!seen.has(site.id)) {
        seen.add(site.id);
        merged.push(site);
      }
    }

    return merged
      .map(addDistance)
      .sort((a, b) => (a.distance || 999) - (b.distance || 999));
  }, [
    isSearchActive,
    searchSites,
    scheduledSites,
    assignedSites,
    userLocation,
  ]);

  const isLoading = isSearchActive ? isSearching : isLoadingAssigned;

  const handleSiteSelection = (site: Site) => {
    setSelectedSite(site);
  };

  const startInspection = () => {
    if (selectedSite) {
      navigate(`/lite/inspect/${selectedSite.id}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 pb-20">
      <div className="bg-primary-600 dark:bg-primary-700 text-white p-4">
        <h1 className="text-xl font-semibold">Start New Inspection</h1>
        <p className="text-primary-100 dark:text-primary-200 mt-1">
          Select a site to begin
        </p>
      </div>

      <div className="p-4 space-y-4">
        {isLoadingLocation && (
          <div className="bg-primary-50 dark:bg-primary-900/30 p-4 rounded-lg flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 dark:border-primary-400"></div>
            <span className="text-sm text-slate-700 dark:text-slate-300">
              Getting your location...
            </span>
          </div>
        )}

        {locationError && (
          <div className="bg-warning-50 dark:bg-warning-900/30 p-4 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-warning-800 dark:text-warning-200">
                {locationError}
              </p>
              <button
                onClick={getCurrentLocation}
                className="text-sm text-primary-600 dark:text-primary-400 underline mt-1"
              >
                Try again
              </button>
            </div>
          </div>
        )}

        {userLocation && !isLoadingLocation && (
          <div className="bg-success-50 dark:bg-success-900/30 p-4 rounded-lg flex items-center gap-3">
            <Navigation className="w-5 h-5 text-success-600 dark:text-success-400" />
            <span className="text-sm text-success-800 dark:text-success-200">
              Location detected - showing nearest sites
            </span>
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500 w-5 h-5" />
          <input
            type="text"
            placeholder="Search all sites by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
          />
        </div>

        <div className="space-y-3">
          <h2 className="font-medium text-slate-900 dark:text-white">
            {isSearchActive
              ? 'Search Results'
              : userLocation
                ? 'Your Sites (nearest first)'
                : 'Your Sites'}
          </h2>

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600 dark:text-primary-400" />
              <span className="ml-2 text-slate-600 dark:text-slate-400">
                {isSearchActive ? 'Searching...' : 'Loading sites...'}
              </span>
            </div>
          )}

          {!isLoading && displaySites.length === 0 && (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              {isSearchActive
                ? `No sites found matching "${debouncedSearch}"`
                : 'No assigned sites found.'}
            </div>
          )}

          {!isLoading &&
            displaySites.map((site) => (
              <button
                key={site.id}
                onClick={() => handleSiteSelection(site)}
                className={`w-full text-left bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm border-2 transition-all ${
                  selectedSite?.id === site.id
                    ? 'border-primary-500 dark:border-primary-400 shadow-md'
                    : 'border-transparent hover:shadow-md dark:hover:bg-slate-700/50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-slate-900 dark:text-white">
                        {site.name}
                      </h3>
                      {site.source === 'scheduled' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Today
                        </span>
                      )}
                      {site.source === 'assigned' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          Assigned
                        </span>
                      )}
                      {selectedSite?.id === site.id && (
                        <CheckCircle className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      )}
                    </div>
                    {site.address && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                        {site.address}
                      </p>
                    )}
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {site.city}
                      {site.city && site.state ? ', ' : ''}
                      {site.state}
                    </p>

                    <div className="flex items-center gap-4 mt-2">
                      {site.distance !== undefined && (
                        <span className="text-sm text-primary-600 dark:text-primary-400 flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {site.distance.toFixed(1)} mi
                        </span>
                      )}
                      {site.lastInspection && (
                        <span className="text-sm text-slate-500 dark:text-slate-400">
                          Last:{' '}
                          {new Date(site.lastInspection).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 dark:text-slate-500 flex-shrink-0" />
                </div>
              </button>
            ))}
        </div>

        {selectedSite && (
          <div className="fixed bottom-20 left-4 right-4 bg-white dark:bg-slate-800 p-4 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700">
            <div className="mb-3">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Selected Site:
              </p>
              <p className="font-medium text-slate-900 dark:text-white">
                {selectedSite.name}
              </p>
            </div>
            <button
              onClick={startInspection}
              className="w-full bg-primary-600 dark:bg-primary-500 text-white py-3 rounded-lg font-medium hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors"
            >
              Start Inspection
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
